<script setup>
import useVuelidate from "@vuelidate/core"
import {required} from "@vuelidate/validators"
import {computed, onMounted, reactive, ref, watch} from "vue"

import InputCounter from "@/components/form/InputCounter.vue"
import InputText from "@/components/form/InputText.vue"
import PaymentMethodRadioGroup from '@/components/form/PaymentMethodRadioGroup.vue'
import FunnelStep from "@/components/FunnelStep.vue"
import FunnelInvoiceAddress from "@/components/funnelSteps/FunnelInvoiceAddress.vue"
import FunnelStepLogin from "@/components/funnelSteps/FunnelStepLogin.vue"
import FunnelStepSummary from "@/components/funnelSteps/FunnelStepSummary.vue"
import { FunnelStepId } from '@/funnelStepId'
import {useAddressStore} from "@/stores/AddressStore"
import {useFormDataStore} from "@/stores/FormDataStore"
import {usePaymentGatewaysStore} from "@/stores/PaymentGatewaysStore"
import {useSubscriptionsStore} from "@/stores/SubscriptionsStore"
import {useUserStore} from "@/stores/UserStore"
import {useVoucherStore} from "@/stores/VoucherStore"
import {useFunnelSteps} from "@/useFunnelSteps"
import {useHelpers} from "@/useHelpers"

const addressStore = useAddressStore()
const dataStore = useFormDataStore()
const paymentGatewayStore = usePaymentGatewaysStore()
const subscriptionStore = useSubscriptionsStore()
const userStore = useUserStore()
const voucherStore = useVoucherStore()

const {getInvoiceAddress} = useHelpers()

const isPromoCodeValid = computed(() => {
  if (state.isPromoCodeVisible) {
    return !!voucherStore.voucher
  }
  return true
})

const maxValidSubscribers = computed(() => {
  return window.settings.version === 'SK' ? 5 : 10
})

const state = reactive({
  isPromoCodeVisible: false,
  isAddressEditing: false,
  isInvoiceAddressEditing: false,
  isDigitalSubscriptionInfoVisible: true,
  isDiscountOfferInfoVisible: true,
  subscriptionAmount: 2,
  steps: [
    {
      id: FunnelStepId.SUBSCRIPTION,
      isActive: false,
      isCompleted: true,
    },
    {
      id: FunnelStepId.SUBSCRIPTION_AMOUNT,
      isActive: true,
      isCompleted: false,
    },
    {
      id: FunnelStepId.ACCOUNT,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.ADDRESS,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.PAYMENT_METHOD,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.SUMMARY,
      isActive: false,
      isCompleted: false,
    },
  ],
  invoiceAddress: getInvoiceAddress(addressStore.invoiceAddress),
  email: addressStore.email,
  promoCode: '',
  paymentGateway: '',
})

const $vVoucherCode = useVuelidate({
  promoCode: {required}
}, state)

const funnelInvoiceAddress = ref(null)

const {
  handleCompeteStep,
  handleChangeStep,
  assignStepsOrder,
  subscriptionStep,
  subscriptionAmountStep,
  accountStep,
  addressStep,
  paymentMethodStep,
  summaryStep,
} = useFunnelSteps(state.steps)

const handlePaymentGatewayUpdate = (resetPromoCode) => {
  if (resetPromoCode) {
    state.isPromoCodeVisible = false
  }

  if (state.isPromoCodeVisible) {
    $vVoucherCode.value.$touch()

    if ($vVoucherCode.value.$invalid || !voucherStore.voucher) {
      return
    }
  }

  handleCompeteStep(FunnelStepId.PAYMENT_METHOD)
}
const handleUsePromoCode = () => {
  voucherStore.validateVoucher(state.promoCode, dataStore.selectedSubscription.code)
}

onMounted(() => {
  assignStepsOrder(state.steps)

  if (addressStore.email) {
    handleCompeteStep(FunnelStepId.ACCOUNT)
  }

  if (addressStore.hasInvoiceAddress && funnelInvoiceAddress.value.isValid()) {
    handleCompeteStep(FunnelStepId.ADDRESS)
  }

  state.paymentGateway = paymentGatewayStore.first.code

  dataStore.setInvoiceAddress(state.invoiceAddress)
  dataStore.setSubscriptionAmount(state.subscriptionAmount)
  subscriptionStore.setAmount(state.subscriptionAmount)
})

watch(
    () => state.paymentGateway,
    (value) => {
      return dataStore.setSelectedPaymentGateway(value)
    }
)

watch(
  () => state.invoiceAddress,
  (newInvoiceAddress) => {
    dataStore.setInvoiceAddress(newInvoiceAddress)
    addressStore.invoiceAddressDraft = newInvoiceAddress
  },
  { deep: true }
)

watch(
    () => state.subscriptionAmount,
    (amount) => {
      subscriptionStore.setAmount(amount)
      dataStore.setSubscriptionAmount(amount)
    }
)

watch(
  () => voucherStore.freeVoucher,
  (free) => {
    state.paymentGateway = free
      ? paymentGatewayStore.freeGatewayCode
      : paymentGatewayStore.first.code
  },
)

</script>

<template>
  <div
      v-if="state.isDigitalSubscriptionInfoVisible"
      class="page-sales-funnel__information-box page-sales-funnel__information-box--default"
  >
    <div class="information-box__wrapper">

      <div class="information-box__inner">
        <h5 class="information-box__title">{{ $t('How do you receive a gift digital subscription?') }}</h5>

        <div class="information-box__description">
          {{ $t('The digital subscription is sent in the form of an activation link. We can send it to the recipient for you. We will also send the activation link to your e-mail, in case you want to share the subscription with other channels.') }}
        </div>

        <a
            @click="state.isDigitalSubscriptionInfoVisible = false"
            class="information-box__link h-cursor--pointer"
        >
          {{ $t('Understood') }}
        </a>
      </div>
    </div>
  </div>

  <FunnelStep
      :id="subscriptionStep.order"
      :details="$t('Subscription')"
      :name="dataStore.selectedSubscription.user_label"
      :is-completed="subscriptionStep.isCompleted"
      :is-active="subscriptionStep.isActive"
      @on-change-clicked="dataStore.setSelectedSubscription('')"
  >
  </FunnelStep>

  <FunnelStep
      :id="subscriptionAmountStep.order"
      :name="`${state.subscriptionAmount} ${$t('users')}`"
      :details="$t('Number of subscriptions')"
      :is-active="subscriptionAmountStep.isActive"
      :is-completed="subscriptionAmountStep.isCompleted"
      @on-change-clicked="handleChangeStep(FunnelStepId.SUBSCRIPTION_AMOUNT)"
  >
    <InputCounter
        v-model="state.subscriptionAmount"
        :min="2"
    />

    <div
        v-if="state.isDiscountOfferInfoVisible && state.subscriptionAmount >= maxValidSubscribers"
        class="page-sales-funnel__information-box page-sales-funnel__information-box--discount"
    >
      <div class="information-box__wrapper">
        <div class="information-box__inner">
          <div
              class="information-box__description"
              v-html="$t('If you order more than 10 subscriptions, we will be happy to offer you more favorable price conditions. For more information, contact the Forbes sales department at email-eshop.', {email: $t('email-eshop')})"
          />

          <a
              @click="state.isDiscountOfferInfoVisible = false"
              class="information-box__link h-cursor--pointer"
          >
            {{ $t('Understood') }}
          </a>
        </div>
      </div>
    </div>

    <!--    <div class="step-numbers__content">-->
    <!--      <div class="step-numbers__option">-->
    <!--        <div class="step-numbers__user">2 - 5 uživatelů</div>-->
    <!--        <div class="step-numbers__price">127 Kč / user <span class="discount">(-20%)</span></div>-->
    <!--      </div>-->

    <!--      <div class="step-numbers__option active">-->
    <!--        <div class="step-numbers__user">6 - 10 uživatelů</div>-->
    <!--        <div class="step-numbers__price">110 Kč / user <span class="discount">(-30%)</span></div>-->
    <!--      </div>-->

    <!--      <div class="step-numbers__option">-->
    <!--        <div class="step-numbers__user">10 a víc uživatelů</div>-->
    <!--        <div class="step-numbers__price">100 Kč / user <span class="discount">(-40%)</span></div>-->
    <!--      </div>-->
    <!--    </div>-->

    <div class="input-group button-group">
      <button
          @click="handleCompeteStep(FunnelStepId.SUBSCRIPTION_AMOUNT)"
          class="button button--large button--primary step-account__button">
        {{ $t('Continue') }}
      </button>
    </div>
  </FunnelStep>

  <FunnelStepLogin
      :id="accountStep.order"
      :details="$t('Account')"
      :name="userStore.currentEmail || state.email"
      :is-active="accountStep.isActive"
      :is-completed="accountStep.isCompleted"
      @on-click-complete-step="handleCompeteStep(FunnelStepId.ACCOUNT)"
      @on-change-clicked="handleChangeStep(FunnelStepId.ACCOUNT)"
  />

  <FunnelStep
      :id="addressStep.order"
      :name="addressStore.invoiceAddressShort"
      :details="$t('Billing address')"
      :is-active="addressStep.isActive"
      :is-completed="addressStep.isCompleted"
      @on-change-clicked="handleChangeStep(FunnelStepId.ADDRESS)"
  >
    <div class="input-group__label-wrapper">
      <div
          class="input-group__content-wrapper active"
          id="paymentContent2"
      >
        <FunnelInvoiceAddress
            ref="funnelInvoiceAddress"
            v-model="state.invoiceAddress"
            :submit-label="$t('Continue to payment method')"
            always-show-company
            @on-successful-update="handleCompeteStep(FunnelStepId.ADDRESS)"
        />
      </div>
    </div>
  </FunnelStep>

  <FunnelStep
      :id="paymentMethodStep.order"
      :name="paymentGatewayStore.getGatewayByCode(state.paymentGateway)?.name || ''"
      :details="$t('Payment method')"
      :is-active="paymentMethodStep.isActive"
      :is-completed="paymentMethodStep.isCompleted"
      @on-change-clicked="handleChangeStep(FunnelStepId.PAYMENT_METHOD)"
  >
    <div class="step-payment-method__form">
      <PaymentMethodRadioGroup
        v-if="!voucherStore.freeVoucher"
        v-model:paymentGateway="state.paymentGateway"
      />

      <div class="input-group checkbox-group">
        <div class="checkbox checkbox--medium">
          <input
              v-model="state.isPromoCodeVisible"
              type="checkbox"
              class="checkbox__input"
              :disabled="voucherStore.voucher"
              id="paymentCheckbox1"
              data-content="paymentContent1"
          />
          <span class="checkbox__indicator"></span>
        </div>

        <div class="input-group__label-wrapper active" id="paymentContent1">
          <label class="checkbox__label" for="paymentCheckbox1">{{ $t('I have promo code') }}</label>
          <p class="input-group__description">{{ $t('You can use one promo code per order') }}</p>

          <div
              class="input-group__content-wrapper"
              id="paymentContent1"
              :class="{
              'active': state.isPromoCodeVisible
            }"
          >
            <template v-if="voucherStore.voucher">
              <div style="display: flex;">
                <strong style="text-decoration: underline;">{{ $vVoucherCode.promoCode.$model }}
                  {{ voucherStore.discountString }}</strong>
                <div @click="voucherStore.resetVoucher" style="cursor: pointer; padding-left: 50px;">X</div>
              </div>
            </template>
            <template v-else>
              <InputText
                  v-model="$vVoucherCode.promoCode.$model"
                  @keyup="voucherStore.resetErrorResponse"
                  :validator="$vVoucherCode.promoCode"
                  :placeholder="$t('Enter promo code')"
                  :error="voucherStore.error"
              />
              <button
                  @click="handleUsePromoCode"
                  :class="{
                  'button button--large button--primary step-payment-method__button': true,
                  'disabled': voucherStore.isSubmitting
                }"
              >
                {{ $t('Use promo code') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!state.isInvoiceAddressEditing && !isPromoCodeValid" class="input-group button-group">
      <button
          @click="handlePaymentGatewayUpdate(true)"
          class="button button--large button--primary step-payment-method__button"
      >
        {{ $t('Continue to the summary without promo code') }}
      </button>
    </div>

    <div v-if="!state.isInvoiceAddressEditing && isPromoCodeValid" class="input-group button-group">
      <button
          @click="handlePaymentGatewayUpdate(false)"
          class="button button--large button--primary step-payment-method__button"
      >
        {{ $t('Continue to the summary') }}
      </button>
    </div>
  </FunnelStep>

  <FunnelStepSummary
      :id="summaryStep.order"
      :is-active="summaryStep.isActive"
      :is-completed="summaryStep.isCompleted"
  />
</template>